import React, { useState, useEffect } from 'react';
import { inventoryAPI, projectAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  Building2, Home, Plus, Search, CheckCircle2, XCircle, Lock,
  Layers, IndianRupee, Ruler, Filter
} from 'lucide-react';

const InventoryPage = () => {
  const { hasRole } = useAuth();
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedBhk, setSelectedBhk] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [newUnit, setNewUnit] = useState({
    project_id: '',
    unit_number: '',
    floor: '',
    bhk_type: '',
    carpet_area: '',
    price: '',
  });

  const canManage = hasRole(['builder_admin']);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [unitsRes, projectsRes] = await Promise.all([
        inventoryAPI.list(),
        projectAPI.list(),
      ]);
      setUnits(unitsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUnit = async (e) => {
    e.preventDefault();
    try {
      await inventoryAPI.create({
        ...newUnit,
        floor: newUnit.floor ? parseInt(newUnit.floor) : null,
        carpet_area: newUnit.carpet_area ? parseFloat(newUnit.carpet_area) : null,
        price: newUnit.price ? parseFloat(newUnit.price) : null,
      });
      toast.success('Unit added to inventory');
      setDialogOpen(false);
      setNewUnit({ project_id: '', unit_number: '', floor: '', bhk_type: '', carpet_area: '', price: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add unit');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'available': { class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Available' },
      'blocked': { class: 'bg-amber-100 text-amber-700', icon: Lock, label: 'Blocked' },
      'sold': { class: 'bg-red-100 text-red-700', icon: XCircle, label: 'Sold' },
    };
    const style = styles[status] || styles.available;
    const Icon = style.icon;
    return (
      <Badge className={style.class}>
        <Icon className="h-3 w-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const formatPrice = (price) => {
    if (!price) return 'Price on request';
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown';
  };

  // Get unique BHK types
  const bhkTypes = [...new Set(units.map(u => u.bhk_type).filter(Boolean))];

  const filteredUnits = units.filter((u) => {
    const matchesProject = selectedProject === 'all' || u.project_id === selectedProject;
    const matchesBhk = selectedBhk === 'all' || u.bhk_type === selectedBhk;
    const matchesStatus = selectedStatus === 'all' || u.status === selectedStatus;
    const matchesSearch = u.unit_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesProject && matchesBhk && matchesStatus && matchesSearch;
  });

  const stats = {
    total: units.length,
    available: units.filter(u => u.status === 'available').length,
    blocked: units.filter(u => u.status === 'blocked').length,
    sold: units.filter(u => u.status === 'sold').length,
  };

  // Group by floor for floor plan view
  const floorGroups = filteredUnits.reduce((acc, unit) => {
    const floor = unit.floor || 0;
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(unit);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Inventory Management</h1>
          <p className="text-muted-foreground">Real-time unit availability and pricing</p>
        </div>
        
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-add-unit">
                <Plus className="h-4 w-4 mr-2" />
                Add Unit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add Inventory Unit</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUnit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Project *</Label>
                  <Select value={newUnit.project_id} onValueChange={(v) => setNewUnit({...newUnit, project_id: v})} required>
                    <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Unit Number *</Label>
                    <Input value={newUnit.unit_number} onChange={(e) => setNewUnit({...newUnit, unit_number: e.target.value})} required placeholder="e.g., A-1001" />
                  </div>
                  <div className="space-y-2">
                    <Label>Floor</Label>
                    <Input type="number" value={newUnit.floor} onChange={(e) => setNewUnit({...newUnit, floor: e.target.value})} placeholder="e.g., 10" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>BHK Type *</Label>
                  <Select value={newUnit.bhk_type} onValueChange={(v) => setNewUnit({...newUnit, bhk_type: v})} required>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1 BHK">1 BHK</SelectItem>
                      <SelectItem value="2 BHK">2 BHK</SelectItem>
                      <SelectItem value="3 BHK">3 BHK</SelectItem>
                      <SelectItem value="4 BHK">4 BHK</SelectItem>
                      <SelectItem value="5 BHK">5 BHK</SelectItem>
                      <SelectItem value="Penthouse">Penthouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Carpet Area (sq.ft)</Label>
                    <Input type="number" value={newUnit.carpet_area} onChange={(e) => setNewUnit({...newUnit, carpet_area: e.target.value})} placeholder="e.g., 1250" />
                  </div>
                  <div className="space-y-2">
                    <Label>Price (₹)</Label>
                    <Input type="number" value={newUnit.price} onChange={(e) => setNewUnit({...newUnit, price: e.target.value})} placeholder="e.g., 7500000" />
                  </div>
                </div>
                <Button type="submit" className="w-full">Add Unit</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Layers className="h-4 w-4" />
              <span className="text-sm">Total Units</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="summary-card border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">Available</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{stats.available}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Lock className="h-4 w-4" />
              <span className="text-sm">Blocked</span>
            </div>
            <p className="text-2xl font-bold">{stats.blocked}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <XCircle className="h-4 w-4" />
              <span className="text-sm">Sold</span>
            </div>
            <p className="text-2xl font-bold">{stats.sold}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search unit number..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-[180px]">
            <Building2 className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Project" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedBhk} onValueChange={setSelectedBhk}>
          <SelectTrigger className="w-[140px]">
            <Home className="h-4 w-4 mr-2" />
            <SelectValue placeholder="BHK" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All BHK</SelectItem>
            {bhkTypes.map((bhk) => <SelectItem key={bhk} value={bhk}>{bhk}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-[150px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Floor-wise View */}
      {Object.keys(floorGroups).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No units found</p>
            <p className="text-muted-foreground">Add inventory units to track availability</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.keys(floorGroups).sort((a, b) => b - a).map((floor) => (
            <Card key={floor}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Floor {floor}
                  <Badge variant="outline">{floorGroups[floor].length} units</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {floorGroups[floor].map((unit) => (
                    <div 
                      key={unit.id} 
                      className={`p-3 rounded-lg border-2 transition-all cursor-pointer hover:shadow-md ${
                        unit.status === 'available' 
                          ? 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-400' 
                          : unit.status === 'blocked'
                          ? 'border-amber-200 bg-amber-50/50'
                          : 'border-red-200 bg-red-50/50 opacity-75'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm">{unit.unit_number}</span>
                        {unit.status === 'available' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                        {unit.status === 'blocked' && <Lock className="h-4 w-4 text-amber-600" />}
                        {unit.status === 'sold' && <XCircle className="h-4 w-4 text-red-600" />}
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">{unit.bhk_type}</p>
                      {unit.carpet_area && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Ruler className="h-3 w-3" />
                          {unit.carpet_area} sq.ft
                        </p>
                      )}
                      <p className="text-sm font-semibold mt-2 flex items-center">
                        <IndianRupee className="h-3 w-3" />
                        {unit.price ? formatPrice(unit.price).replace('₹', '') : 'TBD'}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-3">Legend</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-emerald-400 bg-emerald-50"></div>
              <span className="text-sm">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-amber-400 bg-amber-50"></div>
              <span className="text-sm">Blocked / On Hold</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-red-400 bg-red-50"></div>
              <span className="text-sm">Sold</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryPage;
